import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang, INSTAGRAM_URL } from "@/lib/i18n";
import { useSite } from "@/lib/site";

export const useNavItems = () => {
  const { config } = useSite();
  const { t, lang } = useLang();
  return config.sections
    .filter((s) => s.visible)
    .map((s) => ({ id: s.key, label: s.custom ? s[`title_${lang}`] || s.title_en : t(`nav.${s.key}`) }))
    .filter((n) => n.label);
};

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className="flex border border-[var(--line)] font-mono text-[11px] tracking-[0.15em]">
      {[["en", "EN"], ["pt", "PT"]].map(([v, l]) => (
        <button key={v} data-testid={`lang-${v}`} onClick={() => setLang(v)} aria-pressed={lang === v}
          className={`px-2.5 py-1.5 transition-colors ${lang === v ? "bg-white text-black" : "text-[var(--bone)]/80 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
};

export const InstagramLink = ({ className = "", testid = "instagram-link" }) => {
  const { t } = useLang();
  return (
    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid={testid} aria-label={t("instagram")}
      className={`inline-flex items-center justify-center hover:text-white hover:scale-110 transition-[color,transform] ${className}`}>
      <Instagram className="w-5 h-5" />
    </a>
  );
};

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Nav = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = (id) => { setOpen(false); scrollToId(id); };
  const NAV = useNavItems();
  const hasGear = NAV.some((n) => n.id === "gear");

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || open ? "bg-black/85 backdrop-blur-md border-[var(--line)]" : "bg-transparent border-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-5 sm:px-8 lg:px-12 h-16 sm:h-20">
        <button data-testid="nav-logo" onClick={() => go("top")} aria-label="Back to top"><Logo /></button>
        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-link-${n.id}`}
              onClick={() => go(n.id)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-[var(--bone)]/80 hover:text-[var(--bone)] transition-colors"
            >
              {n.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-full bg-[var(--signal)] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3 sm:gap-4">
          <InstagramLink className="hidden sm:inline-flex text-[var(--bone)]/80" testid="nav-instagram-link" />
          <LangToggle />
          {hasGear && <button
            onClick={() => go("gear")}
            className="hidden xl:inline-flex clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[var(--bone)] transition-colors"
          >
            {t("nav.shop")}
          </button>}
          <button data-testid="nav-mobile-toggle" className="xl:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            className="xl:hidden overflow-hidden border-t border-[var(--line)]"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {NAV.map((n, i) => (
                <button key={n.id} data-testid={`nav-mobile-link-${n.id}`} onClick={() => go(n.id)}
                  className="text-left font-display text-4xl font-extrabold uppercase">
                  <span className="font-mono text-xs text-[var(--signal)] mr-3 align-middle">0{i + 1}</span>{n.label}
                </button>
              ))}
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-instagram-link"
                className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--bone)]/85 pt-2">
                <Instagram className="w-5 h-5" /> @arrow.development.group
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
